/* eslint-disable no-prototype-builtins */
import { compact, uniq } from '@linc-technologies/linced-common/utils/arrays';

// pronounMap is a map of known pronouns to gender codes.
const pronounMap: Readonly<Record<string, string>> = {
  he: 'M',
  him: 'M',
  his: 'M',
  himself: 'M',
  she: 'F',
  her: 'F',
  herself: 'F',
  hers: 'F',
  they: 'N',
  their: 'N',
  theirs: 'N',
  them: 'N',
  themself: 'N',
  themselves: 'N',
  firstname: 'N',
};

// pronounMatcher is a regular expression which matches slash-separated sets of
// pronouns, which can then be replaced with the applicable one.
const pronounMatcher =
  /([\w']+(?: (?:is|are|was|were))?)\/([\w']+(?: (?:is|are|was|were))?)(?:\/([\w']+(?: (?:is|are|was|were))?))?/gi;

// basePronoun strips off any suffixes from the provided pronoun, for looking
// up in pronounMap without requiring every possible permutation to be mapped.
function basePronoun(pronoun: string): string {
  // split() always returns at least one item.
  return (pronoun.split(/[ ']/)[0] as string).toLowerCase();
}

// pronounHasExtension checks if the provided pronoun has a suffix/extension.
function pronounHasExtension(chunk: string): boolean {
  return /[ ']/.test(chunk);
}

// replacePronouns replaces slash-separated sets of pronouns with the set
// member which matches the specified target gender, or wraps the set to flag
// it when previewing without a specified person.
export function replacePronouns(
  content: string,
  targetGender: string | null,
  neutralGenderIncluded: boolean,
  preview: boolean | undefined,
  previewClass: string,
): string {
  const matches = Array.from(content.matchAll(pronounMatcher));
  const replacements: [string, string][] = [];

  matches.forEach((match) => {
    const chunks = compact(match);
    if (
      neutralGenderIncluded
        ? chunks.length !== 4
        : chunks.length < 3 || chunks.length > 4
    ) {
      // Match is not valid, return early
      return;
    }

    // Pull the pattern out of the array (checked non-empty above)
    let pattern = chunks.shift() as string;
    // Check that each pronoun is valid
    const valid = chunks.every((chunk) => {
      return pronounMap.hasOwnProperty(basePronoun(chunk));
    });
    if (!valid) {
      return;
    }
    // At least two pronouns remain after the shift.
    const lastChunk = chunks.at(-1) as string;
    if (pronounHasExtension(lastChunk)) {
      const remainingChunks = chunks.slice(0, -1);
      if (!remainingChunks.some((chunk) => pronounHasExtension(chunk))) {
        const pronoun = lastChunk.split(/[ ']/)[0] as string;
        chunks.splice(chunks.length - 1, 1, pronoun);
        pattern = chunks.join('/');
      }
    }

    if (preview) {
      // If this is preview content, return the pattern wrapped in a class
      const wrappedToken = `<span class="${previewClass}" contenteditable="false">${pattern}</span>`;
      replacements.push([pattern, wrappedToken]);
      return;
    }
    // Otherwise, determine the correctly gendered pronoun
    const replacement = chunks.find((chunk) => {
      const base = basePronoun(chunk);
      return pronounMap[base] === targetGender;
    });

    if (replacement) {
      replacements.push([pattern, replacement]);
    }
  });

  // TS-BUG: uniq() compares by identity, and each pair is a new array, so it
  // removes nothing.
  // In preview mode a repeated pattern is then wrapped in nested spans.
  uniq(replacements)
    .sort((a, b) => {
      return a[0] < b[0] ? 1 : -1;
    })
    .forEach(([pattern, replacement]) => {
      content = content.replaceAll(pattern, replacement);
    });
  return content;
}
